/**
 * Load a third-party script in the browser for the given url.
 *
 * @param url (array or string) - the url (or urls) to load. Will be used in the "src" attribute for the <script> tag
 * @param options (object):
 * @param options.debug (bool) - set to true to enable verbose log messaging. Defaults to false
 * @param options.async (bool) - set to true to load the script with the "async" tag. Defaults to true
 * @returns (promise) - resolves with an array of loaded scripts when all the script's onload callbacks have fired
 */
const allLoaded = []; // keep track of all scripts loaded across all calls for debugging

// can't destructure options because "async" is a keyword
const load = async (url, {
  type,
  async = true,
  appendTo = 'head',
  debug = false,
} = {},
) => {
  // keep track of loaded resources
  const loaded = [];

  const loadSingle = src =>
    new Promise((resolve, reject) => {
      const fileExtension = src.split(/[#?]/)[0].split('.').pop().trim();
      const isJs = fileExtension === 'js';
      const isCss = fileExtension === 'css';
      if (!(isJs || isCss)) {
        throw new Error('third-party-scripts can only load JS and CSS files.');
      }
      let _type = type;
      if (!type) {
        if (isJs) {
          _type = 'application/javascript';
        } else if (isCss) {
          _type = 'text/css';
        }
      }
      let s;
      if (isJs) {
        s = document.createElement('script');
      } else if (isCss) {
        s = document.createElement('link');
        s.setAttribute('rel', 'stylesheet');
      }


      s.async = async;
      s.type = _type;

      s.onload = () => {
        if (debug) {
          // eslint-disable-next-line no-console
          console.log('finished loading', src);
          allLoaded.push(src);
        }
        loaded.push(src);
        resolve();
      };

      s.onerror = (err) => {
        if (debug) {
          // eslint-disable-next-line no-console
          console.error('rejecting promise', err);
        }

        reject(new Error(`Failed to load ${src}`));
      };

      if (debug) {
        // eslint-disable-next-line no-console
        console.log('starting to load', src);
      }
      if (isJs) {
        s.src = src;
      } else {
        s.href = src;
      }
      document.getElementsByTagName(appendTo)[0].appendChild(s);
    });

  if (!url) {
    throw new Error('thirdPartyScripts.load() requires a url (or array of urls)');
  }

  if (Array.isArray(url)) {
    const loadMultiple = [];
    url.forEach(src => loadMultiple.push(loadSingle(src)));
    await Promise.all(loadMultiple);
    return loaded;
  }

  await loadSingle(url);
  return loaded[0];
};

// return a clone of the array
const getAllLoaded = () => allLoaded.slice(0);

export { load, getAllLoaded };

